import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getIsSubscriptionDialogOpen, setIsSubscriptionDialogOpen } from "../reducers/subscription/subscriptionReducer";
import { SubscriptionProviderInstance } from "../@shared/services/SubscriptionProvider";
import { LicenseConfiguration, LicensePrices, LicenseValue } from "../@shared/Models/License";
import { NotificationModelType, NotificationModel } from "../reducers/main/mainModel";
import { onThrowAlert } from "../sagas/dataSaga/dataSagaActions";
import { UserProviderInstance } from "../@shared/services/UserProvider";
import { setIsAuthorized } from "../reducers/main/mainReducer";


export type useSubscriptionProps = {

}

export type useSubscriptionResult = {
    setIsDialogOpen: (value: boolean) => void;
    isDialogOpen: boolean;
    subscriptions: LicenseValue[],
    isLoadingSubscriptions: boolean,
    onLoadPrice: (conf: LicenseConfiguration) => Promise<LicensePrices>,
    onUpdateLicense: (conf: LicenseConfiguration) => Promise<void>,
    isProcessing: boolean,
    onApplyTrial: () => Promise<void>,
}

export const useSubscription = (): useSubscriptionResult => {
    const dispatch = useDispatch();
    const isDialogOpen = useSelector(getIsSubscriptionDialogOpen);
    const [subscriptions, setSubscriptions] = useState<LicenseValue[]>([]);
    const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const setIsDialogOpen = useCallback((value: boolean) => {
        dispatch(setIsSubscriptionDialogOpen(value));
    }, [dispatch]);
    const subscriptionProviderInstance = useMemo(() => SubscriptionProviderInstance.getInstance(), [])
    const userProviderInstance = useMemo(() => UserProviderInstance, [])

    const onLoadSubscriptions = useCallback(async () => {
        setIsLoadingSubscriptions(true);
        const list = await subscriptionProviderInstance.getList();
        setSubscriptions(list.data);
        setIsLoadingSubscriptions(false);
    }, [subscriptionProviderInstance]);

    const onLoadPrice = useCallback(async (conf: LicenseConfiguration) => {
        return (await subscriptionProviderInstance.getPrice(conf)).data;
    }, [subscriptionProviderInstance]);

    const onUpdateLicense = useCallback(async (conf: LicenseConfiguration) => {
        try {
            setIsProcessing(true);
            var res = await subscriptionProviderInstance.onUpdate(conf);
            if (res.status !== 200) {
                dispatch(onThrowAlert({
                    message: res.data.toString(),
                    lifeTime: 5000,
                    type: NotificationModelType.Error
                } as NotificationModel));
            }
            else {
                setIsDialogOpen(false);
                dispatch(onThrowAlert({
                    message: "Subscription succesfully updated",
                    lifeTime: 5000,
                    type: NotificationModelType.Success
                } as NotificationModel));
                const currentUser = await userProviderInstance.current()
                dispatch(setIsAuthorized(currentUser.data));
            }
        }
        catch (e) {
            dispatch(onThrowAlert({
                message: e,
                lifeTime: 5000,
                type: NotificationModelType.Error
            } as NotificationModel));
        }
        finally {
            setIsProcessing(false);
        }
    }, [dispatch, setIsDialogOpen, subscriptionProviderInstance, userProviderInstance]);

    const onApplyTrial = useCallback(async () => {
        setIsProcessing(true);
        var res = await subscriptionProviderInstance.applyTrial();
        if (res.status !== 200) {
            dispatch(onThrowAlert({
                message: res.data.toString(),
                lifeTime: 5000,
                type: NotificationModelType.Error
            } as NotificationModel));
        }
        else {
            setIsDialogOpen(false);
            dispatch(onThrowAlert({
                message: "Subscription succesfully updated",
                lifeTime: 5000,
                type: NotificationModelType.Success
            } as NotificationModel));
            const currentUser = await userProviderInstance.current()
            dispatch(setIsAuthorized(currentUser.data));
        }
        setIsProcessing(false);
    }, [dispatch, setIsDialogOpen, subscriptionProviderInstance, userProviderInstance])

    useEffect(() => {
        if (!isDialogOpen) {
            return;
        }
        onLoadSubscriptions();
    }, [isDialogOpen, onLoadSubscriptions])

    return {
        setIsDialogOpen,
        isDialogOpen,
        subscriptions,
        isLoadingSubscriptions,
        onLoadPrice,
        onUpdateLicense,
        isProcessing,
        onApplyTrial
    }
}